import * as THREE from 'three';
import { CinematicCamera } from './camera';
import { CharacterRig } from './CharacterRig';
import { clamp01, pulse, smoothstep, smootherstep, vec3 } from './math';
import {
  buildEnvironment,
  createGround,
  createShadowPowers,
  DeterministicParticles,
  evaluateEnvironment,
  evaluateShadowPowers,
} from './procedural';
import { ResourceTracker } from './resources';
import { createChapterAssetSet } from './ProductionAssets';
import {
  createJesusCrucifixionBody,
  createUniversalHeroBase,
  type UniversalHeroKind,
} from './UniversalHeroAssets';
import {
  createCharcoalBrazierWithFish,
  createClayJar,
  createLowDiningTableAndCushions,
  createOliveOilLamp,
  createRomanJudgmentSeatAndStandard,
  createTombBierAndLinen,
  createWovenBasket,
  createWovenMat,
} from './FirstCenturyProps';
import type {
  ChapterId,
  ChapterPreset,
  ChapterScene,
  CameraShot,
  QualityProfile,
  TimelineState,
} from './types';

export class CinematicChapterScene implements ChapterScene {
  readonly id: ChapterId;
  readonly scene = new THREE.Scene();
  readonly camera: THREE.PerspectiveCamera;
  private readonly preset: ChapterPreset;
  private readonly cameraDirector: CinematicCamera;
  private readonly resourceTracker = new ResourceTracker();
  private readonly environment: THREE.Group;
  private readonly ground: THREE.Mesh;
  private readonly characters = new Map<string, CharacterRig>();
  private readonly particles: DeterministicParticles[] = [];
  private readonly shadowPowers: THREE.Group;
  private readonly keyLight: THREE.DirectionalLight;
  private readonly ambient: THREE.HemisphereLight;
  private readonly fillLight: THREE.DirectionalLight;
  private readonly rimLight: THREE.PointLight;
  private readonly lightVeil: THREE.Group;
  private readonly storyProps = new THREE.Group();
  private productionSet?: THREE.Group;
  private quality: QualityProfile;
  private entered = false;
  private plateTexture?: THREE.Texture;
  private plateMesh?: THREE.Mesh<THREE.PlaneGeometry, THREE.MeshBasicMaterial>;
  private readonly shotPlateTextures = new Map<string, THREE.Texture>();
  private shotPlateMesh?: THREE.Mesh<THREE.PlaneGeometry, THREE.MeshBasicMaterial>;

  constructor(preset: ChapterPreset, quality: QualityProfile) {
    this.id = preset.id;
    this.preset = preset;
    this.quality = quality;
    this.scene.name = `chapter:${preset.id}`;
    this.scene.background = new THREE.Color(preset.palette.sky);
    this.scene.fog = new THREE.FogExp2(preset.palette.fog, preset.fogDensity * quality.fogMultiplier);

    this.cameraDirector = new CinematicCamera(preset.shots);
    this.camera = this.cameraDirector.camera;

    this.environment = buildEnvironment(preset, quality);
    this.ground = createGround(preset, quality);
    this.scene.add(this.ground, this.environment);
    // Camera children are required for the overscanned camera-relative plate.
    this.scene.add(this.camera);

    for (const spec of preset.characters) {
      const character = new CharacterRig(spec);
      this.characters.set(spec.id, character);
      this.scene.add(character);
    }

    for (const spec of preset.particles) {
      // Allocate the authored maximum once so runtime quality upgrades can
      // restore every particle through drawRange without rebuilding scenes.
      const count = Math.max(8, spec.count);
      const particles = new DeterministicParticles(spec, count, `${preset.id}:${spec.kind}`);
      this.particles.push(particles);
      this.scene.add(particles);
    }

    this.shadowPowers = createShadowPowers();
    this.scene.add(this.shadowPowers);

    this.keyLight = new THREE.DirectionalLight(preset.light.color, preset.light.intensity);
    this.keyLight.name = 'lighting:key';
    this.keyLight.position.copy(vec3(preset.light.position));
    this.keyLight.target.position.copy(vec3(preset.light.target));
    this.keyLight.castShadow = quality.shadows;
    this.configureShadows();
    this.scene.add(this.keyLight, this.keyLight.target);

    this.ambient = new THREE.HemisphereLight(preset.light.ambientColor, preset.palette.ground, preset.light.ambientIntensity);
    this.ambient.name = 'lighting:ambient';
    this.scene.add(this.ambient);

    // Non-shadowing fill preserves faces while the key keeps a sculpted silhouette.
    this.fillLight = new THREE.DirectionalLight(
      new THREE.Color(preset.light.ambientColor).lerp(new THREE.Color(0x8ba3bd), 0.28),
      preset.light.ambientIntensity * 1.35,
    );
    this.fillLight.position.set(9, 7, 6);
    this.fillLight.target.position.set(0, 1.25, -2);
    this.fillLight.castShadow = false;
    this.scene.add(this.fillLight, this.fillLight.target);

    // Warm edge light separates robes and faces from the deep sets.
    this.rimLight = new THREE.PointLight(preset.palette.accent, preset.id === 'silence' ? 2.2 : 4.2, 20, 1.7);
    this.rimLight.position.set(-4.5, 3.2, 2.8);
    this.rimLight.castShadow = false;
    this.scene.add(this.rimLight);

    this.lightVeil = this.createLightVeil(preset.palette.accent);
    this.storyProps.name = `story-props:${this.id}`;
    this.buildStoryProps();
    this.scene.add(this.lightVeil, this.storyProps);
    this.setQuality(quality);
    this.resourceTracker.trackTree(this.scene);
  }

  async load(): Promise<void> {
    // The default film is authored and rendered as actual Three.js geometry.
    // Painterly plates are retained only as an explicit comparison/debug mode;
    // they must never replace the interactive 3D sets in the released film.
    const plateMode = new URLSearchParams(window.location.search).get('plates') === '1';
    if (!plateMode) {
      await this.loadProductionAssets();
      return;
    }
    try {
      const texture = await new THREE.TextureLoader().loadAsync(`/assets/cinematic-plates/${this.id}-v2.png`);
      texture.colorSpace = THREE.SRGBColorSpace;
      texture.wrapS = texture.wrapT = THREE.ClampToEdgeWrapping;
      texture.anisotropy = 8;
      const material = new THREE.MeshBasicMaterial({
        map: texture,
        depthTest: false,
        depthWrite: false,
        toneMapped: false,
        fog: false,
      });
      const plate = new THREE.Mesh(new THREE.PlaneGeometry(128, 72), material);
      plate.name = `cinematic-plate:${this.id}`;
      plate.position.set(0, 0, -40);
      plate.renderOrder = -10_000;
      plate.frustumCulled = false;
      this.camera.add(plate);
      this.plateTexture = texture;
      this.plateMesh = plate;
      this.resourceTracker.track(texture);
      this.resourceTracker.trackTree(plate);

      const authoredShotPlates: Partial<Record<ChapterId, string[]>> = {
        'bread-and-night': ['bread-hands'],
        'garden-and-judgment': ['agony-close'],
        golgotha: ['mary-witness'],
        'garden-reopened': ['mary-name'],
        'eyes-opened': ['thomas-wounds', 'peter-fire'],
      };
      const shotIds = authoredShotPlates[this.id] ?? [];
      await Promise.all(shotIds.map(async (shotId) => {
        const shotTexture = await new THREE.TextureLoader().loadAsync(`/assets/cinematic-plates/${this.id}--${shotId}-v2.png`);
        shotTexture.colorSpace = THREE.SRGBColorSpace;
        shotTexture.wrapS = shotTexture.wrapT = THREE.ClampToEdgeWrapping;
        shotTexture.anisotropy = 8;
        this.shotPlateTextures.set(shotId, shotTexture);
        this.resourceTracker.track(shotTexture);
      }));
      const firstShotTexture = this.shotPlateTextures.values().next().value as THREE.Texture | undefined;
      if (firstShotTexture) {
        const shotMaterial = new THREE.MeshBasicMaterial({
          map: firstShotTexture,
          transparent: true,
          opacity: 0,
          depthTest: false,
          depthWrite: false,
          toneMapped: false,
          fog: false,
        });
        const shotPlate = new THREE.Mesh(new THREE.PlaneGeometry(128, 72), shotMaterial);
        shotPlate.name = `cinematic-shot-plate:${this.id}`;
        shotPlate.position.set(0, 0, -39.98);
        shotPlate.renderOrder = -9_999;
        shotPlate.frustumCulled = false;
        this.camera.add(shotPlate);
        this.shotPlateMesh = shotPlate;
        this.resourceTracker.trackTree(shotPlate);
      }
      // The plate contains natural figures and a complete historical set.
      // Keep only atmospheric Three.js layers in front of it.
      this.environment.visible = false;
      this.ground.visible = false;
      this.lightVeil.visible = false;
      for (const character of this.characters.values()) character.visible = false;
    } catch (error) {
      console.warn(`Cinematic plate unavailable for ${this.id}; using procedural fallback.`, error);
    }
  }

  private async loadProductionAssets(): Promise<void> {
    try {
      const set = await createChapterAssetSet(this.id);
      this.productionSet = set;
      this.scene.add(set);
      if (this.id === 'garden-reopened') {
        // The detailed modular arch is the released tomb facade. Removing the
        // fallback mound opens the camera corridor while preserving true 3D.
        const mound = this.environment.getObjectByName('set:tomb-mound');
        const door = this.environment.getObjectByName('set:tomb-door');
        if (mound) mound.visible = false;
        if (door) door.visible = false;
      }
    } catch (error) {
      console.warn(`Production 3D assets unavailable for ${this.id}; using procedural fallback.`, error);
    }

    // Every named principal now uses the same coherent shared skeleton, hands,
    // neck and garment construction. Procedural capsule figures are retained
    // only as distant crowd LOD and for the dedicated crucifixion anatomy.
    const principalRoles = new Set<UniversalHeroKind>(['jesus', 'mary', 'peter', 'judas', 'pilate']);
    const principals = [...this.characters.values()].filter(
      (character) => character.spec.id !== 'mother'
        && (principalRoles.has(character.spec.role as UniversalHeroKind)
          || (this.id === 'eyes-opened' && character.spec.id === 'disciple-2')
          || (this.id === 'golgotha' && character.spec.id === 'simon')),
    );
    try {
      await Promise.all(principals.map(async (character) => {
        // The younger clean-shaven Judas presentation doubles as Thomas only
        // in the later, non-overlapping appearance chapter. Costume and staging
        // remain authored by Thomas's own CharacterSpec.
        const kind = character.spec.role === 'disciple'
          ? 'judas'
          : character.spec.id === 'simon'
            ? 'peter'
            : character.spec.role as UniversalHeroKind;
        if (kind === 'mary') {
          // This asset is rebuilt from the actual female peasant rig. It keeps
          // shoulder, arm and hand skinning continuous while the runtime long
          // robe replaces only the lower silhouette.
          const mary = await createUniversalHeroBase('mary');
          character.attachUniversalHero(mary, true, Boolean(mary.userData.detailsOnly));
          this.resourceTracker.trackTree(character);
          return;
        }
        const base = await createUniversalHeroBase(kind);
        character.attachUniversalHero(base, true, Boolean(base.userData.detailsOnly));
        this.resourceTracker.trackTree(character);
        if (this.id === 'golgotha' && kind === 'jesus') {
          const crucifixionBody = await createJesusCrucifixionBody();
          character.attachCrucifixionBody(crucifixionBody);
          this.resourceTracker.trackTree(crucifixionBody);
        }
      }));
    } catch (error) {
      console.warn(`Detailed principal cast unavailable for ${this.id}; using procedural cast.`, error);
    }
  }

  enter(_previous?: ChapterId): void {
    this.entered = true;
  }

  evaluate(state: TimelineState): void {
    const time = Math.min(this.preset.duration, Math.max(0, state.localTime));
    const reducedMotion = state.reducedMotion ?? false;
    const shot = this.cameraDirector.evaluate(time, reducedMotion);
    this.scene.userData.exposure = shot?.exposure ?? 1;

    this.evaluateChapter(time, reducedMotion);
    this.evaluateStoryProps(time);
    evaluateEnvironment(this.environment, time, reducedMotion);
    for (const particle of this.particles) particle.evaluate(time, reducedMotion);
    for (const character of this.characters.values()) character.evaluate(time);
    this.evaluateCharacterPlacement(time);
    if (this.id === 'golgotha' && time >= 26) {
      const witnessTarget = new THREE.Vector3(0, 3.38, -3.05);
      this.characters.get('mary')?.orientHeadToward(witnessTarget);
      this.characters.get('witness-1')?.orientHeadToward(witnessTarget);
    }
    if (this.plateTexture) {
      this.evaluatePlateFraming(shot, time, reducedMotion);
      for (const character of this.characters.values()) character.visible = false;
      this.shadowPowers.visible = false;
    }
    this.evaluateLightVeil(time, reducedMotion);
    this.keyLight.target.updateMatrixWorld();
  }

  private evaluatePlateFraming(shot: CameraShot | undefined, time: number, reducedMotion: boolean): void {
    if (!this.plateTexture || !this.plateMesh) return;
    const zoomFor = (candidate: CameraShot | undefined): number => {
      if (!candidate) return 1.04;
      switch (candidate.rig) {
        case 'close': return 1.27;
        case 'pov': return 1.2;
        case 'handheld': return 1.15;
        case 'orbit': return 1.13;
        case 'dolly': return 1.1;
        case 'crane': return 1.025;
        default: return 1.045;
      }
    };
    const shotIndex = shot ? this.preset.shots.indexOf(shot) : 0;
    const previous = shotIndex > 0 ? this.preset.shots[shotIndex - 1] : undefined;
    const transition = shot ? smootherstep((time - shot.start) / 1.35) : 1;
    const zoom = THREE.MathUtils.lerp(zoomFor(previous), zoomFor(shot), transition);
    const repeat = 1 / Math.min(1.3, Math.max(1, zoom));
    const margin = (1 - repeat) * 0.5;
    const shotPan = Math.sin((shotIndex + 1) * 2.17 + this.id.length) * margin * 0.62;
    const priorPan = Math.sin(Math.max(1, shotIndex) * 2.17 + this.id.length) * margin * 0.62;
    const drift = reducedMotion ? 0.00035 : Math.sin(time * 0.055 + this.id.length) * Math.min(0.0025, margin * 0.15);
    const panX = THREE.MathUtils.lerp(priorPan, shotPan, transition) + drift;
    const panY = Math.cos((shotIndex + 1) * 1.73) * margin * 0.17;
    this.plateTexture.repeat.set(repeat, repeat);
    this.plateTexture.offset.set(margin + panX, margin + panY);

    // Fit the 16:9 plate to the active lens before applying the UV crop above.
    const distance = Math.abs(this.plateMesh.position.z);
    const visibleHeight = 2 * Math.tan(THREE.MathUtils.degToRad(this.camera.fov * 0.5)) * distance;
    const visibleWidth = visibleHeight * this.camera.aspect;
    this.plateMesh.scale.set((visibleWidth / 128) * 1.012, (visibleHeight / 72) * 1.012, 1);

    if (this.shotPlateMesh) {
      const shotTexture = shot ? this.shotPlateTextures.get(shot.id) : undefined;
      const material = this.shotPlateMesh.material;
      if (shot && shotTexture) {
        if (material.map !== shotTexture) {
          material.map = shotTexture;
          material.needsUpdate = true;
        }
        const fadeIn = smootherstep((time - shot.start) / 0.8);
        const fadeOut = 1 - smootherstep((time - (shot.end - 0.8)) / 0.8);
        material.opacity = Math.min(fadeIn, fadeOut) * 0.985;
        shotTexture.repeat.set(1, 1);
        shotTexture.offset.set(0, 0);
      } else {
        material.opacity = 0;
      }
      this.shotPlateMesh.scale.copy(this.plateMesh.scale);
    }
  }

  exit(_next?: ChapterId): void {
    this.entered = false;
  }

  setQuality(profile: QualityProfile): void {
    this.quality = profile;
    this.keyLight.castShadow = profile.shadows;
    this.configureShadows();
    if (this.scene.fog instanceof THREE.FogExp2) {
      this.scene.fog.density = this.preset.fogDensity * profile.fogMultiplier;
    }
    this.particles.forEach((particle, index) => {
      const sourceCount = this.preset.particles[index]?.count ?? particle.geometry.getAttribute('position').count;
      const available = particle.geometry.getAttribute('position').count;
      particle.geometry.setDrawRange(0, Math.min(available, Math.round(sourceCount * profile.particleMultiplier)));
    });
  }

  resize(width: number, height: number): void {
    this.cameraDirector.resize(width, height);
  }

  dispose(): void {
    this.resourceTracker.dispose();
    this.characters.clear();
    this.particles.length = 0;
  }

  private evaluateChapter(time: number, reducedMotion: boolean): void {
    let shadowStrength = 0;
    let lightScale = 1;
    let fogScale = 1;

    switch (this.id) {
      case 'veil': {
        const reveal = smoothstep(time / 18);
        lightScale = 0.12 + reveal * 0.88;
        fogScale = 1.35 - reveal * 0.35;
        shadowStrength = (1 - reveal) * 0.55;
        const memory = this.characters.get('jesus-memory');
        if (memory) memory.visible = time >= 8 && time < 30;
        const witness = this.characters.get('witness-memory');
        if (witness) witness.visible = time >= 11 && time < 28;
        break;
      }
      case 'bread-and-night': {
        const jesus = this.characters.get('jesus');
        jesus?.setPose(time >= 11 && time < 21 ? 'kneeling' : time > 21 && time < 30 ? 'blessing' : 'seated');
        const peter = this.characters.get('peter');
        peter?.setPose(time >= 11 && time < 21 ? 'seated' : 'seated');
        const judas = this.characters.get('judas');
        judas?.setPose(time > 37 ? 'walking' : 'seated');
        const isolateExit = time >= 38 && time < 51;
        for (const id of ['jesus', 'mary', 'peter', 'disciple-1', 'disciple-2']) {
          const character = this.characters.get(id);
          if (character) character.visible = !isolateExit;
        }
        if (judas) judas.visible = true;
        lightScale = 1 - smoothstep((time - 40) / 15) * 0.38;
        break;
      }
      case 'garden-and-judgment': {
        if (this.productionSet) this.productionSet.visible = false;
        this.setEnvironmentVisibility('set:garden', time < 31);
        this.setEnvironmentVisibility('set:courtyard', time >= 31);
        const jesus = this.characters.get('jesus');
        jesus?.setPose(time < 21 ? 'kneeling' : 'bound');
        const judas = this.characters.get('judas');
        if (judas) {
          judas.visible = time > 17 && time < 31;
          judas.setPose(time >= 21 && time < 25.5 ? 'reaching' : 'walking');
        }
        const soldiers = [this.characters.get('soldier-1'), this.characters.get('soldier-2')];
        for (const soldier of soldiers) if (soldier) soldier.visible = time >= 21;
        const pilate = this.characters.get('pilate');
        if (pilate) pilate.visible = time >= 42;
        const peter = this.characters.get('peter');
        if (peter) peter.visible = time < 21 || (time >= 31 && time < 42);
        peter?.setPose(time < 21 || time > 37 ? 'mourning' : 'still');
        for (const id of ['disciple-1', 'disciple-2']) {
          const disciple = this.characters.get(id);
          if (disciple) {
            // During judgment these are two separated, distant followers—not
            // an accusing crowd. Roman soldiers occupy the coercive foreground.
            disciple.visible = time < 21 || time >= 42;
            disciple.setPose('mourning');
          }
        }
        const judgmentDawn = smoothstep((time - 40) / 5);
        lightScale = THREE.MathUtils.lerp(0.9, 1.58, judgmentDawn);
        if (this.scene.background instanceof THREE.Color) {
          this.scene.background.lerpColors(new THREE.Color(this.preset.palette.sky), new THREE.Color(0x4b515b), judgmentDawn);
        }
        if (this.scene.fog instanceof THREE.FogExp2) {
          this.scene.fog.color.lerpColors(new THREE.Color(this.preset.palette.fog), new THREE.Color(0x3e444d), judgmentDawn);
        }
        if (time < 21) {
          this.keyLight.color.setHex(0xa9c9ec);
          this.rimLight.color.setHex(0xb76a32);
        } else {
          this.keyLight.color.setHex(this.preset.light.color);
          this.rimLight.color.setHex(this.preset.palette.accent);
        }
        break;
      }
      case 'golgotha': {
        if (this.productionSet) this.productionSet.visible = time < 26;
        this.setEnvironmentVisibility('set:road', time < 26);
        this.setEnvironmentVisibility('set:golgotha', time >= 26);
        const leftCross = this.environment.getObjectByName('set:cross:-3.2');
        const rightCross = this.environment.getObjectByName('set:cross:3.2');
        if (leftCross) leftCross.visible = time >= 106;
        if (rightCross) rightCross.visible = false;
        const jesus = this.characters.get('jesus');
        if (jesus) jesus.setPose(time >= 26 ? 'cruciform' : 'burdened');
        const mary = this.characters.get('mary');
        if (mary) mary.visible = time < 26 || (time >= 54 && time < 106);
        const simon = this.characters.get('simon');
        if (simon) {
          simon.visible = time >= 6 && time < 26;
          simon.setPose(time >= 12 ? 'supporting' : 'walking');
        }
        for (const id of ['soldier-1', 'soldier-2']) {
          const soldier = this.characters.get(id);
          soldier?.setPose(time < 26 ? 'walking' : 'still');
          if (soldier) soldier.visible = time < 106;
        }
        shadowStrength = smoothstep((time - 68) / 8) * (1 - smoothstep((time - 91) / 10));
        lightScale = 1.12 - shadowStrength * 0.34 + smoothstep((time - 106) / 6) * 0.24;
        fogScale = 1 + shadowStrength * 0.48;
        break;
      }
      case 'silence': {
        const burialLabor = time < 13;
        const mary = this.characters.get('mary');
        const peter = this.characters.get('peter');
        const mother = this.characters.get('mother');
        mary?.setPose(burialLabor ? 'reaching' : 'mourning');
        peter?.setPose(burialLabor ? 'reaching' : 'mourning');
        mother?.setPose('mourning');
        // Reuse the established Mary principal for the short city-grief insert.
        // Loading a second full Mary hero as "mother" produced a visibly cloned,
        // ghosted pair in the 286-316 second range.
        if (mary) mary.visible = time < 29 || (time >= 34 && time < 40) || time >= 52;
        if (peter) peter.visible = time < 24 || (time >= 29 && time < 34);
        if (mother) mother.visible = false;
        // The tomb belongs only to burial/sealing. Sabbath, VISION, and first
        // light each get their own spatial grammar instead of recycling it.
        this.setEnvironmentVisibility('set:tomb', time < 24);
        this.setEnvironmentVisibility('set:city', time >= 24 && time < 40 || time >= 52);
        if (this.productionSet) this.productionSet.visible = time < 24;
        shadowStrength = pulse(time, 46, 7) * 0.8;
        lightScale = 0.62 + smoothstep((time - 51) / 9) * 0.45;
        fogScale = 1.2 - smoothstep((time - 51) / 9) * 0.2;
        break;
      }
      case 'garden-reopened': {
        const dawn = smoothstep(time / 35);
        lightScale = 0.58 + dawn * 0.52;
        fogScale = 1.38 - dawn * 0.54;
        const jesus = this.characters.get('jesus');
        if (jesus) {
          jesus.visible = time >= 26;
          jesus.setPose(time >= 42 && time < 55 ? 'blessing' : 'still');
          jesus.setRetainedWounds(time >= 55);
        }
        const mary = this.characters.get('mary');
        const linenInsert = time >= 18 && time < 24;
        if (mary) mary.visible = !linenInsert;
        mary?.setPose(time < 14 ? 'walking' : time >= 42 && time < 55 ? 'reaching' : 'mourning');
        for (const id of ['witness-1', 'witness-2']) {
          const witness = this.characters.get(id);
          if (witness) {
            witness.visible = time < 26 && !linenInsert;
            witness.setPose(time < 14 ? 'walking' : 'mourning');
          }
        }
        break;
      }
      case 'eyes-opened': {
        // Each encounter receives its own space: garden testimony, enclosed
        // Emmaus/Thomas interiors, then shoreline and mountain light.
        this.setEnvironmentVisibility('set:garden', time < 23);
        this.setEnvironmentVisibility('set:shore', time >= 54);
        const shoreFire = this.environment.getObjectByName('set:shore-fire');
        if (shoreFire) shoreFire.visible = false;
        if (this.productionSet) this.productionSet.visible = time >= 23 && time < 54;
        const jesus = this.characters.get('jesus');
        const mary = this.characters.get('mary');
        const peter = this.characters.get('peter');
        const firstDisciple = this.characters.get('disciple-1');
        const thomas = this.characters.get('disciple-2');
        if (jesus) {
          jesus.setPose(time >= 23 && time < 37 ? 'seated' : time >= 37 && time < 54 ? 'blessing' : time >= 54 && time < 66 ? 'reaching' : 'still');
          jesus.setRetainedWounds(time >= 37 && time < 54);
        }
        mary?.setPose(time < 23 ? 'reaching' : 'still');
        // Peter's restoration is played as bowed, grounded grief rather than a
        // second palms-forward hero pose that made him read as Jesus's double.
        peter?.setPose(time >= 54 && time < 66 ? 'mourning' : 'still');
        firstDisciple?.setPose(time >= 23 && time < 37 ? 'seated' : 'still');
        thomas?.setPose(time >= 23 && time < 37 ? 'seated' : time >= 37 && time < 54 ? 'reaching' : 'still');
        lightScale = 0.98 + smoothstep((time - 66) / 19) * 0.35;
        fogScale = 1 - smoothstep((time - 66) / 19) * 0.28;
        break;
      }
    }

    evaluateShadowPowers(this.shadowPowers, time, shadowStrength, reducedMotion);
    const veilCity = this.id === 'veil' && time >= 25 && time < 38;
    const veilProfile = this.id === 'veil' && time >= 38;
    const gardenNight = this.id === 'garden-and-judgment' && time < 31;
    const denialCourtyard = this.id === 'garden-and-judgment' && time >= 31 && time < 42;
    const romanTrial = this.id === 'garden-and-judgment' && time >= 42;
    const golgothaRoad = this.id === 'golgotha' && time < 26;

    let ambientReadability = 1;
    let fillReadability = 1;
    let fogReadability = 1;
    let rimIntensity = (this.id === 'silence' ? 1.7 : 3.4) * (0.72 + lightScale * 0.28);

    // Reset all authored positions every evaluation so a reverse seek cannot
    // inherit a camera-relative night fill from a later shot.
    this.fillLight.position.set(9, 7, 6);
    this.fillLight.target.position.set(0, 1.25, -2);
    this.rimLight.position.set(-4.5, 3.2, 2.8);

    if (veilCity || veilProfile) {
      ambientReadability = veilProfile ? 1.9 : 2.25;
      fillReadability = veilProfile ? 4 : 3.1;
      fogReadability = veilProfile ? 0.82 : 0.7;
      rimIntensity = veilProfile ? 4.8 : 3;
      this.fillLight.position.set(this.camera.position.x, this.camera.position.y + 2.2, this.camera.position.z + 1.8);
      this.fillLight.target.position.set(veilProfile ? -1.3 : 0, veilProfile ? 1.7 : 2.3, veilProfile ? -1.7 : -8);
      this.rimLight.position.set(veilProfile ? 1.2 : -3.5, veilProfile ? 2.3 : 4.8, veilProfile ? -0.4 : -4.5);
    } else if (this.id === 'bread-and-night') {
      ambientReadability = 3.05;
      fillReadability = 3.45;
    } else if (gardenNight) {
      ambientReadability = 2.85;
      fillReadability = 3.2;
      rimIntensity = time < 21 ? 0.92 : 2.8;
    } else if (denialCourtyard || romanTrial) {
      ambientReadability = romanTrial ? 1.9 : 1.85;
      fillReadability = romanTrial ? 5.2 : 4.1;
      fogReadability = romanTrial ? 0.6 : 0.66;
      rimIntensity = romanTrial ? 5 : 4.2;
      this.fillLight.position.set(this.camera.position.x, this.camera.position.y + 2.4, this.camera.position.z + 1.6);
      this.fillLight.target.position.set(romanTrial ? 0 : -1.7, romanTrial ? 1.7 : 1.4, romanTrial ? -3.8 : -0.7);
      this.rimLight.position.set(romanTrial ? -3.8 : 2.8, 3.1, romanTrial ? -1.5 : 1.2);
    } else if (golgothaRoad) {
      ambientReadability = 2.2;
      fillReadability = 3.3;
      fogReadability = 0.84;
      rimIntensity = 4.6;
      this.fillLight.position.set(this.camera.position.x, this.camera.position.y + 2.6, this.camera.position.z + 1.8);
      this.fillLight.target.position.set(0, 1.4, -1.1);
      this.rimLight.position.set(-4.8, 3.4, 3.6);
    }

    this.keyLight.intensity = this.preset.light.intensity * lightScale * (golgothaRoad ? 1.08 : romanTrial ? 1.08 : denialCourtyard ? 1.03 : veilCity || veilProfile ? 1.06 : 1);
    this.ambient.intensity = this.preset.light.ambientIntensity * (0.42 + lightScale * 0.16) * ambientReadability;
    this.fillLight.intensity = this.preset.light.ambientIntensity * (0.24 + lightScale * 0.16) * fillReadability;
    this.rimLight.intensity = rimIntensity;
    if (this.scene.fog instanceof THREE.FogExp2) {
      this.scene.fog.density = this.preset.fogDensity * this.quality.fogMultiplier * fogScale * fogReadability;
    }
  }

  /** Apply timeline translations after rigs reset themselves to their authored base pose. */
  private evaluateCharacterPlacement(time: number): void {
    // Reset authored transforms on every evaluation so seeking backward is
    // identical to uninterrupted playback.
    for (const character of this.characters.values()) {
      character.position.copy(vec3(character.spec.position));
      character.rotation.y = character.spec.rotationY ?? 0;
      character.rotation.x = 0;
      character.rotation.z = 0;
    }

    if (this.id === 'bread-and-night') {
      const jesus = this.characters.get('jesus');
      const peter = this.characters.get('peter');
      if (time >= 11 && time < 21) {
        if (jesus) {
          jesus.position.set(-0.55, -0.03, 0.62);
          jesus.rotation.y = Math.PI / 2;
        }
        if (peter) {
          peter.position.set(0.62, 0.02, 0.58);
          peter.rotation.y = -Math.PI / 2;
        }
      }
      const judas = this.characters.get('judas');
      if (judas && time > 37) {
        const t = clamp01((time - 37) / 3.2);
        judas.position.x = THREE.MathUtils.lerp(judas.spec.position.x, 4.12, smootherstep(t));
        // Keep the shallower skinned torso in front of the doorway wall; the
        // former depth only exposed the forward half of the tunic overlay.
        judas.position.z = THREE.MathUtils.lerp(judas.spec.position.z, -0.74, smootherstep(t));
        judas.rotation.y = THREE.MathUtils.lerp(judas.spec.rotationY ?? 0, -0.48, t);
      }
    } else if (this.id === 'garden-and-judgment') {
      const jesus = this.characters.get('jesus');
      const peter = this.characters.get('peter');
      const firstDisciple = this.characters.get('disciple-1');
      const secondDisciple = this.characters.get('disciple-2');

      if (time < 21) {
        // Jesus is physically low and pitched forward; the three followers
        // remain far enough back to read as exhausted witnesses, not a tableau.
        if (jesus) {
          jesus.position.set(1.15, -0.58, -1.45);
          // Positive pitch brings the chest and head forward toward the earth
          // (negative pitch read as an unintended recline from this camera).
          jesus.rotation.x = 0.58;
          jesus.rotation.z = 0.035;
        }
        if (peter) peter.position.set(-1.65, 0, -6.85);
        if (firstDisciple) firstDisciple.position.set(-2.35, 0, -7.3);
        if (secondDisciple) secondDisciple.position.set(-0.78, 0, -7.15);
      } else if (time < 31) {
        const arrest = smootherstep((time - 21) / 10);
        const judas = this.characters.get('judas');
        const soldierOne = this.characters.get('soldier-1');
        const soldierTwo = this.characters.get('soldier-2');
        if (jesus) {
          jesus.position.set(0, 0, -1.62);
          jesus.rotation.y = 0;
        }
        if (judas) {
          const identify = smootherstep((time - 17) / 7);
          const withdraw = smoothstep((time - 25) / 6);
          judas.position.set(
            THREE.MathUtils.lerp(3.35, 0.82, identify) + withdraw * 1.45,
            0,
            THREE.MathUtils.lerp(0.45, -1.3, identify) + withdraw * 0.22,
          );
          judas.rotation.y = -1.32 + identify * 0.32;
        }
        if (soldierOne) {
          soldierOne.position.set(THREE.MathUtils.lerp(4.6, 1.2, arrest), 0, THREE.MathUtils.lerp(0.2, -1.35, arrest));
          soldierOne.rotation.y = -1.18;
        }
        if (soldierTwo) {
          soldierTwo.position.set(THREE.MathUtils.lerp(-4.4, -1.18, arrest), 0, THREE.MathUtils.lerp(0.05, -1.42, arrest));
          soldierTwo.rotation.y = 1.16;
        }
      } else if (time >= 42) {
        const pilate = this.characters.get('pilate');
        const soldierOne = this.characters.get('soldier-1');
        const soldierTwo = this.characters.get('soldier-2');
        // A strict vertical hierarchy makes the legal mechanism legible:
        // prisoner on the pavement, soldiers flanking him, governor above.
        if (jesus) {
          jesus.position.set(0, 0, -1.55);
          jesus.rotation.y = 0;
          jesus.rotation.x = 0.035;
        }
        if (pilate) {
          pilate.position.set(0, 0.92, -4.5);
          pilate.rotation.y = 0;
        }
        if (soldierOne) {
          soldierOne.position.set(-1.55, 0, -2.15);
          soldierOne.rotation.y = 0.34;
        }
        if (soldierTwo) {
          soldierTwo.position.set(1.55, 0, -2.18);
          soldierTwo.rotation.y = -0.34;
        }
        if (firstDisciple) {
          firstDisciple.position.set(-4.15, 0, -4.95);
          firstDisciple.rotation.y = 0.42;
        }
        if (secondDisciple) {
          secondDisciple.position.set(4.1, 0, -5.1);
          secondDisciple.rotation.y = -0.48;
        }
      }
    } else if (this.id === 'golgotha') {
      const jesus = this.characters.get('jesus');
      if (jesus) {
        const crucified = time >= 26;
        const progress = smootherstep(time / 26);
        const stumble = Math.sin(clamp01((time - 6.2) / 5.5) * Math.PI) * (1 - smoothstep((time - 13) / 3));
        jesus.position.y = crucified ? 2.1 : -stumble * 0.11;
        jesus.position.z = crucified ? -3.05 : THREE.MathUtils.lerp(-2.55, 1.15, progress);
        jesus.rotation.x = crucified ? 0 : 0.12 + stumble * 0.16;
        jesus.rotation.z = crucified ? 0 : -0.035 - stumble * 0.075;
        jesus.rotation.y = 0;
        if (!crucified) {
          const soldierOne = this.characters.get('soldier-1');
          const soldierTwo = this.characters.get('soldier-2');
          if (soldierOne) {
            soldierOne.position.set(-1.72, 0, jesus.position.z - 1.28);
            soldierOne.rotation.y = 0.14;
          }
          if (soldierTwo) {
            soldierTwo.position.set(1.78, 0, jesus.position.z - 1.12);
            soldierTwo.rotation.y = -0.18;
          }
          const simon = this.characters.get('simon');
          if (simon) {
            const enter = smootherstep((time - 6) / 9);
            simon.position.set(THREE.MathUtils.lerp(-4.7, -1.42, enter), 0, jesus.position.z - THREE.MathUtils.lerp(1.1, 0.32, enter));
            simon.rotation.y = THREE.MathUtils.lerp(0.35, 0.04, enter);
          }
        } else {
          const mary = this.characters.get('mary');
          const witness = this.characters.get('witness-1');
          const soldierOne = this.characters.get('soldier-1');
          const soldierTwo = this.characters.get('soldier-2');
          if (mary) {
            const foregroundWitness = time < 54 || time >= 106;
            mary.position.set(
              foregroundWitness ? (time >= 106 ? -3.4 : -4.3) : -2.35,
              0,
              foregroundWitness ? (time >= 106 ? 0.15 : 1.08) : 0.84,
            );
            mary.rotation.y = foregroundWitness ? 2.48 : 2.62;
            mary.rotation.x = 0.025;
          }
          if (witness) {
            witness.position.set(2.75, 0, 0.35);
            witness.rotation.y = -2.55;
          }
          if (soldierOne) {
            soldierOne.position.set(-3.35, 0, -1.48);
            soldierOne.rotation.y = 0.48;
          }
          if (soldierTwo) {
            soldierTwo.position.set(2.45, 0, -1.62);
            soldierTwo.rotation.y = -0.76;
          }
        }
      }
    } else if (this.id === 'silence') {
      if (time < 13) {
        const mary = this.characters.get('mary');
        const peter = this.characters.get('peter');
        if (mary) {
          mary.position.set(-1.62, -0.22, -1.42);
          mary.rotation.set(0.05, -1.18, -0.16);
        }
        if (peter) {
          peter.position.set(1.58, -0.22, -1.48);
          peter.rotation.set(0.05, 1.18, 0.16);
        }
      } else if (time < 24) {
        const mary = this.characters.get('mary');
        const peter = this.characters.get('peter');
        const mother = this.characters.get('mother');
        if (mary) mary.position.set(-2.2, 0, -0.65);
        if (peter) peter.position.set(2.25, 0, -0.7);
        if (mother) mother.position.set(-3.35, 0, -1.9);
      } else if (time < 29) {
        const mary = this.characters.get('mary');
        if (mary) {
          mary.position.set(-1.65, 0, -1.2);
          mary.rotation.y = 0.18;
        }
      } else if (time < 34) {
        const peter = this.characters.get('peter');
        if (peter) {
          peter.position.set(1.65, 0, -1.35);
          peter.rotation.y = -0.18;
        }
      } else if (time < 40) {
        const mary = this.characters.get('mary');
        if (mary) {
          mary.position.set(-0.25, 0, -3.7);
          mary.rotation.y = 0.05;
        }
      } else if (time >= 52) {
        const mary = this.characters.get('mary');
        if (mary) {
          mary.position.set(-1.3, 0, -1.1);
          mary.rotation.y = 0.2;
        }
      }
    } else if (this.id === 'garden-reopened') {
      const mary = this.characters.get('mary');
      const jesus = this.characters.get('jesus');
      const firstWoman = this.characters.get('witness-1');
      const secondWoman = this.characters.get('witness-2');
      if (time < 14) {
        const approach = smootherstep(time / 14);
        if (mary) mary.position.set(-1.25, 0, THREE.MathUtils.lerp(1.1, -0.75, approach));
        if (firstWoman) firstWoman.position.set(-2.35, 0, THREE.MathUtils.lerp(1.75, -0.15, approach));
        if (secondWoman) secondWoman.position.set(-3.25, 0, THREE.MathUtils.lerp(1.45, -0.35, approach));
      } else if (time < 26) {
        if (mary) {
          mary.position.set(-1.25, 0, -1.02);
          mary.rotation.y = 0;
        }
        if (firstWoman) firstWoman.position.set(-2.35, 0, -0.68);
        if (secondWoman) secondWoman.position.set(-3.15, 0, -0.38);
      } else if (time < 34) {
        if (mary) {
          mary.position.set(-0.48, 0, 0.08);
          mary.rotation.y = 2.18;
        }
        if (jesus) {
          jesus.position.set(1.3, 0, 0.0);
          jesus.rotation.y = -1.02;
        }
      } else if (time < 42) {
        const turn = smootherstep((time - 34) / 3.5);
        if (mary) {
          mary.position.set(-0.65, 0, 0.1);
          mary.rotation.y = THREE.MathUtils.lerp(2.18, 1.18, turn);
        }
        if (jesus) {
          jesus.position.set(0.78, 0, 0.04);
          jesus.rotation.y = -1.12;
        }
      } else if (time < 55) {
        const stop = smootherstep((time - 42) / 5);
        if (mary) {
          mary.position.set(THREE.MathUtils.lerp(-0.95, -0.68, stop), 0, -0.2);
          mary.rotation.y = 1.15;
        }
        if (jesus) {
          jesus.position.set(1.0, 0, -0.24);
          jesus.rotation.y = -1.15;
        }
      } else {
        if (mary) {
          mary.position.set(-1.08, 0, -0.28);
          mary.rotation.y = 1.05;
        }
        if (jesus) {
          jesus.position.set(0.92, 0, -0.26);
          jesus.rotation.y = -1.05;
        }
      }
    } else if (this.id === 'eyes-opened') {
      const jesus = this.characters.get('jesus');
      const mary = this.characters.get('mary');
      const peter = this.characters.get('peter');
      const firstDisciple = this.characters.get('disciple-1');
      const thomas = this.characters.get('disciple-2');
      if (time < 23) {
        if (jesus) jesus.visible = false;
        for (const character of [mary, peter, firstDisciple, thomas]) if (character) character.visible = true;
        const receive = smootherstep((time - 8) / 12);
        if (mary) {
          mary.position.set(-1.0, 0, 0.08);
          mary.rotation.y = 1.22;
        }
        if (peter) {
          peter.position.set(1.05, 0, -0.18);
          peter.rotation.y = THREE.MathUtils.lerp(-0.38, -1.2, receive);
        }
        if (firstDisciple) {
          firstDisciple.position.set(-2.65, 0, -1.55);
          firstDisciple.rotation.y = 0.62;
        }
        if (thomas) {
          thomas.position.set(2.75, 0, -1.72);
          thomas.rotation.y = -0.7;
        }
        if (time >= 16) {
          // The testimony eyeline is a clean Mary/Peter two-shot. Distant
          // disciples previously crowded the edges while leaving the dramatic
          // center empty.
          if (mary) {
            mary.position.set(-0.72, 0, -0.02);
            mary.rotation.y = 1.02;
          }
          if (peter) {
            peter.position.set(0.72, 0, -0.08);
            peter.rotation.y = -1.02;
          }
          if (firstDisciple) firstDisciple.visible = false;
          if (thomas) thomas.visible = false;
        }
      } else if (time < 37) {
        if (jesus) jesus.visible = true;
        if (mary) mary.visible = false;
        if (peter) peter.visible = false;
        if (firstDisciple) firstDisciple.visible = true;
        if (thomas) thomas.visible = true;
        if (jesus) {
          jesus.position.set(0, -0.42, -2.78);
          jesus.rotation.y = 0;
        }
        if (firstDisciple) {
          firstDisciple.position.set(-1.18, -0.42, -1.7);
          firstDisciple.rotation.y = 1.18;
        }
        if (thomas) {
          thomas.position.set(1.18, -0.42, -1.72);
          thomas.rotation.y = -1.18;
        }
      } else if (time < 54) {
        if (jesus) jesus.visible = true;
        if (mary) mary.visible = false;
        if (peter) peter.visible = false;
        if (firstDisciple) firstDisciple.visible = false;
        if (thomas) {
          thomas.visible = true;
          const stoppedReach = smootherstep((time - 37) / 9);
          thomas.position.set(THREE.MathUtils.lerp(1.02, 0.72, stoppedReach), 0, -0.22);
          thomas.rotation.y = -1.2;
        }
        if (jesus) {
          jesus.position.set(-0.52, 0, -0.32);
          jesus.rotation.y = 1.16;
        }
      } else if (time < 66) {
        if (jesus) jesus.visible = true;
        if (mary) mary.visible = false;
        if (firstDisciple) firstDisciple.visible = false;
        if (thomas) thomas.visible = false;
        if (peter) {
          peter.visible = true;
          peter.position.set(0.92, -0.08, -0.2);
          peter.rotation.y = -1.23;
          peter.rotation.x = 0.08;
        }
        if (jesus) {
          jesus.position.set(-0.88, 0.05, -0.62);
          jesus.rotation.y = 0.9;
        }
      } else {
        const facesOnly = time >= 72;
        if (jesus) {
          jesus.visible = !facesOnly;
          jesus.position.set(0, 0, -2.35);
          jesus.rotation.y = 0;
        }
        for (const character of [mary, peter, firstDisciple, thomas]) if (character) character.visible = true;
        if (mary) {
          mary.position.set(facesOnly ? -0.62 : -1.08, 0, -0.08);
          mary.rotation.y = facesOnly ? 0.35 : 0.72;
        }
        if (peter) {
          peter.position.set(facesOnly ? 0.62 : 1.08, 0, -0.1);
          peter.rotation.y = facesOnly ? -0.35 : -0.72;
        }
        if (firstDisciple) {
          firstDisciple.position.set(facesOnly ? -1.62 : -2.3, 0, -0.82);
          firstDisciple.rotation.y = 0.5;
        }
        if (thomas) {
          thomas.position.set(facesOnly ? 1.62 : 2.3, 0, -0.85);
          thomas.rotation.y = -0.5;
        }
      }

      // These inserts are locked to the evaluated skinned palm instead of a
      // guessed world coordinate, so seeking and alternate hero assets retain
      // physical contact.
      const jesusPalm = jesus?.getWorldPalm('right');
      if (jesusPalm && time >= 23 && time < 37) {
        const heldBread = this.storyProps.getObjectByName('prop:emmaus-bread-held');
        // The shared-rig palm offset varies by role pose and left the bread
        // visibly hovering. Keep the loaf grounded on the low table instead.
        if (heldBread) heldBread.visible = false;
      }
      if (jesusPalm && time >= 37 && time < 54) {
        const woundMoment = this.storyProps.getObjectByName('prop:thomas-wound-moment');
        // Retained wounds are already authored on Jesus. The auxiliary red
        // marker read as a floating sphere during Thomas/Peter coverage.
        if (woundMoment) woundMoment.visible = false;
      }
    }
  }

  private buildStoryProps(): void {
    const cloth = (color: number) => new THREE.MeshStandardMaterial({ color, roughness: 0.96, metalness: 0 });
    const prop = (geometry: THREE.BufferGeometry, material: THREE.Material) => {
      const mesh = new THREE.Mesh(geometry, material);
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      return mesh;
    };
    const createRomanSpear = (name: string) => {
      const spear = new THREE.Group();
      spear.name = name;
      const shaft = prop(new THREE.CylinderGeometry(0.025, 0.03, 2.75, 9), cloth(0x493326));
      shaft.position.y = 1.38;
      const point = prop(new THREE.ConeGeometry(0.07, 0.29, 8), new THREE.MeshStandardMaterial({ color: 0x756b5a, roughness: 0.72, metalness: 0.25 }));
      point.position.y = 2.9;
      spear.add(shaft, point);
      return spear;
    };

    if (this.id === 'bread-and-night') {
      const dining = createLowDiningTableAndCushions();
      dining.name = 'prop:last-supper-setting';
      dining.position.set(0, 0, -1.75);
      const lamp = createOliveOilLamp(true);
      lamp.position.set(-0.55, 0.62, -1.72);
      const mealJar = createClayJar();
      mealJar.scale.setScalar(0.62);
      mealJar.position.set(2.35, 0, -2.45);
      dining.add(lamp, mealJar);
      this.storyProps.add(dining);

      // A separate threshold keeps Judas visually isolated from the table.
      // The practical lamp is deterministic and extinguishes without implying
      // supernatural possession.
      const threshold = new THREE.Group();
      threshold.name = 'prop:judas-threshold';
      const doorwayStone = cloth(0x8a7862);
      const darkness = prop(
        new THREE.PlaneGeometry(1.72, 2.9),
        new THREE.MeshBasicMaterial({ color: 0x030305, side: THREE.DoubleSide }),
      );
      // Sit behind the actor. At the previous positive-Z offset this matte
      // sliced through the imported skinned torso, leaving only the forward
      // half of the runtime skirt visible in Judas's exit close-up.
      darkness.position.set(0, 1.5, -0.42);
      darkness.visible = false;
      threshold.add(darkness);
      for (const x of [-0.92, 0.92]) {
        const jamb = prop(new THREE.BoxGeometry(0.28, 3.2, 0.34), doorwayStone);
        jamb.position.set(x, 1.6, 0);
        threshold.add(jamb);
      }
      const lintel = prop(new THREE.BoxGeometry(2.12, 0.3, 0.38), doorwayStone);
      lintel.position.set(0, 3.12, 0);
      const sill = prop(new THREE.BoxGeometry(2.02, 0.1, 0.62), doorwayStone);
      sill.position.set(0, 0.05, 0.05);
      const exitLamp = createOliveOilLamp(true);
      exitLamp.name = 'prop:judas-exit-lamp';
      exitLamp.position.set(-0.68, 1.08, 0.26);
      exitLamp.rotation.y = -0.5;
      exitLamp.scale.setScalar(0.72);
      const lampLight = new THREE.PointLight(0xff9d42, 3.8, 7, 1.8);
      lampLight.name = 'prop:judas-exit-lamp-light';
      lampLight.position.set(-0.18, 0.24, 0);
      exitLamp.add(lampLight);
      const lampShelf = prop(new THREE.BoxGeometry(0.62, 0.07, 0.34), cloth(0x665545));
      lampShelf.position.set(-0.68, 1.0, 0.25);
      const thresholdRim = new THREE.PointLight(0x7895bd, 0.58, 5.5, 1.7);
      thresholdRim.name = 'lighting:judas-threshold-rim';
      thresholdRim.position.set(0.65, 2.1, -0.45);
      threshold.position.set(4.12, 0, -1.5);
      threshold.rotation.y = -0.04;
      threshold.add(lintel, sill, lampShelf, exitLamp, thresholdRim);
      this.storyProps.add(threshold);

      const service = new THREE.Group();
      service.name = 'prop:footwashing';
      const bronze = cloth(0x76563a);
      const basin = prop(new THREE.CylinderGeometry(0.36, 0.29, 0.14, 20, 2, true), bronze);
      basin.position.y = 0.14;
      const rim = prop(new THREE.TorusGeometry(0.36, 0.026, 7, 24), bronze);
      rim.rotation.x = Math.PI / 2;
      rim.position.y = 0.22;
      const water = prop(new THREE.CircleGeometry(0.315, 24), new THREE.MeshStandardMaterial({ color: 0x637b80, roughness: 0.2, transparent: true, opacity: 0.8 }));
      water.rotation.x = -Math.PI / 2;
      water.position.y = 0.205;
      const towelMaterial = cloth(0xb5a486);
      towelMaterial.side = THREE.DoubleSide;
      const towel = prop(new THREE.PlaneGeometry(0.3, 0.78, 3, 7), towelMaterial);
      towel.position.set(-0.42, 0.42, 0.08);
      towel.rotation.set(-0.2, 0.2, -0.18);
      const jar = prop(new THREE.CylinderGeometry(0.16, 0.22, 0.46, 14, 4), cloth(0x8b6848));
      jar.position.set(0.52, 0.27, -0.02);
      const jarNeck = prop(new THREE.CylinderGeometry(0.1, 0.13, 0.18, 14), cloth(0x8b6848));
      jarNeck.position.set(0.52, 0.58, -0.02);
      service.position.set(0.02, 0, 1.18);
      service.add(basin, rim, water, towel, jar, jarNeck);
      this.storyProps.add(service);
    }

    if (this.id === 'garden-and-judgment') {
      const night = new THREE.Group();
      night.name = 'prop:gethsemane-night';
      const nightBackdrop = prop(
        new THREE.PlaneGeometry(38, 15),
        new THREE.MeshBasicMaterial({ color: 0x030713, side: THREE.DoubleSide, fog: false }),
      );
      nightBackdrop.position.set(0, 5.2, -12.5);
      nightBackdrop.receiveShadow = false;
      const moonShaft = new THREE.SpotLight(0x9bc8ff, 7.2, 20, 0.34, 0.72, 1.45);
      moonShaft.name = 'lighting:gethsemane-moon-shaft';
      moonShaft.position.set(-3.5, 9.5, 3.5);
      moonShaft.target.position.set(1.15, 0, -1.45);
      moonShaft.castShadow = false;
      night.add(nightBackdrop, moonShaft, moonShaft.target);
      this.storyProps.add(night);

      for (const side of [-1, 1]) {
        const torch = new THREE.Group();
        torch.name = `prop:arrest-torch:${side < 0 ? 'left' : 'right'}`;
        const shaft = prop(new THREE.CylinderGeometry(0.025, 0.035, 1.7, 8), cloth(0x332319));
        shaft.position.y = 0.85;
        const flame = prop(
          new THREE.SphereGeometry(0.075, 12, 8),
          new THREE.MeshStandardMaterial({ color: 0xffb04f, emissive: 0xe35f20, emissiveIntensity: 2.1, roughness: 0.5 }),
        );
        flame.name = 'prop:arrest-torch-flame';
        flame.position.y = 1.82;
        flame.scale.set(0.55, 1.05, 0.55);
        const glow = new THREE.PointLight(0xff8b3d, 2.2, 6, 1.8);
        glow.position.y = 1.75;
        torch.add(shaft, flame, glow);
        this.storyProps.add(torch);
      }

      const judgment = createRomanJudgmentSeatAndStandard();
      judgment.name = 'prop:roman-judgment';
      judgment.position.set(0, 0, -3.6);
      judgment.scale.setScalar(1.12);
      this.storyProps.add(judgment);

      const trialAuthority = new THREE.Group();
      trialAuthority.name = 'prop:trial-roman-authority';
      const leftSpear = createRomanSpear('prop:trial-spear:left');
      leftSpear.position.set(-1.75, 0, -2.28);
      leftSpear.rotation.z = -0.035;
      const rightSpear = createRomanSpear('prop:trial-spear:right');
      rightSpear.position.set(1.75, 0, -2.3);
      rightSpear.rotation.z = 0.035;
      trialAuthority.add(leftSpear, rightSpear);
      this.storyProps.add(trialAuthority);
    }

    if (this.id === 'golgotha') {
      const procession = new THREE.Group();
      procession.name = 'prop:road-procession';
      const timber = new THREE.MeshStandardMaterial({ color: 0x39271d, roughness: 1, metalness: 0, flatShading: true });
      const endGrain = new THREE.MeshStandardMaterial({ color: 0x2b1c15, roughness: 1, metalness: 0, flatShading: true });
      const beam = new THREE.Group();
      beam.name = 'prop:procession-crossbeam';
      const core = prop(new THREE.BoxGeometry(3.65, 0.23, 0.29, 7, 2, 2), timber);
      const mortise = prop(new THREE.BoxGeometry(0.42, 0.255, 0.315), endGrain);
      mortise.position.x = 0.06;
      beam.add(core, mortise);
      procession.add(beam);

      const leftSpear = createRomanSpear('prop:road-spear:left');
      const rightSpear = createRomanSpear('prop:road-spear:right');
      procession.add(leftSpear, rightSpear);
      this.storyProps.add(procession);

      const executionAuthority = new THREE.Group();
      executionAuthority.name = 'prop:execution-roman-authority';
      const executionLeft = createRomanSpear('prop:execution-spear:left');
      executionLeft.position.set(-3.52, 0, -1.62);
      executionLeft.rotation.z = -0.045;
      const executionRight = createRomanSpear('prop:execution-spear:right');
      executionRight.position.set(2.62, 0, -1.78);
      executionRight.rotation.z = 0.04;
      executionAuthority.add(executionLeft, executionRight);
      this.storyProps.add(executionAuthority);

      // A purpose-built witness silhouette avoids pushing Mary's rectangular
      // full mantle into the lens. Her detailed rig is reserved for the grief
      // close; this reads only as a human head and shoulders at frame edge.
      const witnessSilhouette = new THREE.Group();
      witnessSilhouette.name = 'prop:mary-witness-silhouette';
      const silhouetteMaterial = new THREE.MeshBasicMaterial({ color: 0x171820, transparent: true, opacity: 0.82, depthWrite: false });
      const shoulders = prop(new THREE.CapsuleGeometry(0.19, 0.34, 6, 12), silhouetteMaterial);
      shoulders.rotation.z = Math.PI / 2;
      shoulders.scale.set(1, 1.35, 0.62);
      shoulders.position.y = 1.38;
      const head = prop(new THREE.SphereGeometry(0.18, 14, 10), silhouetteMaterial);
      head.scale.set(0.82, 1.03, 0.9);
      head.position.set(0.08, 1.7, 0);
      const veil = prop(new THREE.SphereGeometry(0.215, 14, 9, 0, Math.PI * 2, 0, Math.PI * 0.62), silhouetteMaterial);
      veil.position.set(0.05, 1.76, -0.035);
      veil.scale.set(0.88, 1, 0.9);
      witnessSilhouette.add(shoulders, head, veil);
      this.storyProps.add(witnessSilhouette);
    }

    if (this.id === 'silence') {
      const burial = new THREE.Group();
      burial.name = 'prop:burial-linen-legacy';
      burial.visible = false;
      const linen = cloth(0xb8b1a2);
      const wrappedBody = prop(new THREE.CapsuleGeometry(0.25, 1.3, 7, 12), linen);
      wrappedBody.rotation.z = Math.PI / 2;
      wrappedBody.position.set(0, 0.64, -2.25);
      const headWrap = prop(new THREE.SphereGeometry(0.24, 14, 10), cloth(0xa9a294));
      headWrap.position.set(-0.92, 0.64, -2.25);
      const looseLinenMaterial = cloth(0xc5beb0);
      looseLinenMaterial.side = THREE.DoubleSide;
      const looseLinen = prop(new THREE.PlaneGeometry(1.25, 0.55, 6, 3), looseLinenMaterial);
      looseLinen.rotation.x = -Math.PI / 2;
      looseLinen.position.set(0.35, 0.38, -1.75);
      burial.add(wrappedBody, headWrap, looseLinen);
      this.storyProps.add(burial);
      const tomb = createTombBierAndLinen();
      tomb.name = 'prop:burial-linen';
      tomb.position.set(0, 0, -2.2);
      tomb.rotation.y = Math.PI;
      const mat = createWovenMat(2.6, 1.5);
      mat.position.set(0.35, 0.02, -1.72);
      tomb.add(mat);
      this.storyProps.add(tomb);

      // VISION is an explicitly non-geographic memory space: one isolated
      // human recollection surrounded by the existing non-figural shadow marks.
      const memory = new THREE.Group();
      memory.name = 'prop:vision-memory';
      const silhouette = new THREE.Group();
      silhouette.name = 'prop:vision-human-memory';
      // Layered translucent light volumes and suspended dust read as a memory
      // opening under recognition. The earlier torus fragments resembled a
      // floating UI glyph, which broke the otherwise cinematic visual grammar.
      const shaftLayout = [
        { x: -0.56, y: 1.35, z: -0.28, rz: -0.18, color: 0x746783, opacity: 0.052 },
        { x: 0.02, y: 1.55, z: -0.36, rz: 0.06, color: 0xa193ae, opacity: 0.042 },
        { x: 0.62, y: 1.28, z: -0.22, rz: 0.22, color: 0x665c79, opacity: 0.046 },
      ];
      for (const [index, layout] of shaftLayout.entries()) {
        const material = new THREE.MeshBasicMaterial({
          color: layout.color,
          transparent: true,
          opacity: layout.opacity,
          blending: THREE.AdditiveBlending,
          depthWrite: false,
          side: THREE.DoubleSide,
        });
        material.userData.baseOpacity = layout.opacity;
        const shaft = prop(
          new THREE.ConeGeometry(0.72 + index * 0.13, 3.5 + index * 0.25, 28, 1, true),
          material,
        );
        shaft.castShadow = false;
        shaft.receiveShadow = false;
        shaft.name = `prop:vision-volume:${index}`;
        shaft.position.set(layout.x, layout.y, layout.z);
        shaft.rotation.z = layout.rz;
        shaft.scale.z = 0.5;
        silhouette.add(shaft);
      }

      const dustPositions: number[] = [];
      for (let index = 0; index < 72; index += 1) {
        const phase = index * 2.399963229728653;
        const radius = 0.18 + (index % 13) * 0.055;
        dustPositions.push(
          Math.cos(phase) * radius,
          0.28 + ((index * 17) % 67) / 67 * 2.55,
          Math.sin(phase) * radius * 0.42 - 0.08,
        );
      }
      const dustGeometry = new THREE.BufferGeometry();
      dustGeometry.setAttribute('position', new THREE.Float32BufferAttribute(dustPositions, 3));
      const dustMaterial = new THREE.PointsMaterial({
        color: 0xcabed2,
        size: 0.032,
        sizeAttenuation: true,
        transparent: true,
        opacity: 0.48,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
      });
      dustMaterial.userData.baseOpacity = 0.48;
      const dust = new THREE.Points(dustGeometry, dustMaterial);
      dust.name = 'prop:vision-recognition-dust';
      silhouette.add(dust);

      for (const [index, x] of [-0.48, 0.44].entries()) {
        const fadingShadowMaterial = new THREE.MeshBasicMaterial({
          color: index === 0 ? 0x16101c : 0x211728,
          transparent: true,
          opacity: 0.22,
          depthWrite: false,
          side: THREE.DoubleSide,
        });
        fadingShadowMaterial.userData.baseOpacity = 0.22;
        const fadingShadow = prop(new THREE.PlaneGeometry(0.32, 2.2), fadingShadowMaterial);
        fadingShadow.castShadow = false;
        fadingShadow.receiveShadow = false;
        fadingShadow.name = `prop:vision-fading-accusation:${index}`;
        fadingShadow.position.set(x, 1.15, 0.03);
        fadingShadow.rotation.z = index === 0 ? -0.12 : 0.16;
        silhouette.add(fadingShadow);
      }
      silhouette.position.set(0, 0, -2.05);
      silhouette.rotation.z = -0.045;
      const enteringLight = prop(
        new THREE.PlaneGeometry(1.25, 3.6),
        new THREE.MeshBasicMaterial({ color: 0xc3b7d0, transparent: true, opacity: 0.04, blending: THREE.AdditiveBlending, depthWrite: false }),
      );
      enteringLight.name = 'prop:vision-entering-light';
      enteringLight.visible = true;
      enteringLight.position.set(0.45, 1.55, -2.35);
      enteringLight.rotation.z = -0.18;
      memory.add(silhouette, enteringLight);
      this.storyProps.add(memory);

      const sabbathFrame = new THREE.Group();
      sabbathFrame.name = 'prop:sabbath-city-frame';
      const cityMaterial = cloth(0x24262c);
      for (let index = 0; index < 7; index += 1) {
        const building = prop(new THREE.BoxGeometry(1.05, 1.4 + (index % 3) * 0.48, 0.75), cityMaterial);
        building.position.set((index - 3) * 1.05, 0.7 + (index % 3) * 0.24, -7.2 - (index % 2) * 0.45);
        sabbathFrame.add(building);
      }
      const doorwayEdge = prop(new THREE.BoxGeometry(0.62, 4.8, 0.5), cloth(0x14151a));
      doorwayEdge.name = 'prop:sabbath-doorway-edge';
      doorwayEdge.position.set(-3.65, 2.4, 0.65);
      sabbathFrame.add(doorwayEdge);
      this.storyProps.add(sabbathFrame);
    }

    if (this.id === 'garden-reopened') {
      const emptyTomb = new THREE.Group();
      emptyTomb.name = 'prop:empty-tomb-linen';
      const bench = prop(new THREE.BoxGeometry(2.7, 0.34, 0.92), cloth(0x4c4943));
      bench.position.set(0, 0.33, -3.0);
      const linenMaterial = cloth(0xd7cfbd);
      linenMaterial.side = THREE.DoubleSide;
      const fallenLinen = prop(new THREE.PlaneGeometry(1.35, 0.72, 7, 4), linenMaterial);
      fallenLinen.rotation.x = -Math.PI / 2;
      fallenLinen.rotation.z = 0.18;
      fallenLinen.position.set(-0.22, 0.53, -2.92);
      const faceCloth = prop(new THREE.BoxGeometry(0.48, 0.08, 0.38), cloth(0xc1b7a3));
      faceCloth.position.set(0.82, 0.58, -2.86);
      faceCloth.rotation.y = -0.14;
      emptyTomb.add(bench, fallenLinen, faceCloth);
      for (const [index, x] of [-0.48, -0.08, 0.3].entries()) {
        const fold = prop(new THREE.SphereGeometry(0.36, 14, 8), cloth(index === 1 ? 0xe2daca : 0xd2c8b5));
        fold.scale.set(1.05, 0.18 + index * 0.025, 0.58);
        fold.position.set(x, 0.61 + index * 0.012, -2.9 + (index % 2) * 0.08);
        fold.rotation.y = (index - 1) * 0.22;
        emptyTomb.add(fold);
      }
      this.storyProps.add(emptyTomb);

      const approachDress = new THREE.Group();
      approachDress.name = 'prop:dawn-approach-dress';
      const basket = createWovenBasket();
      basket.scale.setScalar(0.72);
      basket.position.set(-2.85, 0, -0.05);
      approachDress.add(basket);
      const leafMaterial = cloth(0x243729);
      for (const [x, y, z, scale] of [
        [-4.35, 1.0, 1.35, 1.25],
        [-3.8, 2.05, 1.1, 0.82],
        [5.15, 1.15, 1.55, 1.1],
      ] as const) {
        const leaves = prop(new THREE.DodecahedronGeometry(0.72, 0), leafMaterial);
        leaves.position.set(x, y, z);
        leaves.scale.set(scale, scale * 0.78, scale);
        approachDress.add(leaves);
      }
      this.storyProps.add(approachDress);
    }

    if (this.id === 'eyes-opened') {
      const testimonyLight = new THREE.Group();
      testimonyLight.name = 'prop:testimony-volumetric-light';
      // The camera observes this chapter from positive Z; keep the volumes
      // safely behind Mary so they rim her profile instead of intersecting her
      // garment silhouette.
      testimonyLight.position.set(-0.92, 0, -1.65);
      const testimonyShafts = [
        { x: -0.52, z: -0.12, rotation: -0.14, opacity: 0.038, color: 0xffd79a },
        { x: 0.02, z: -0.26, rotation: 0.04, opacity: 0.052, color: 0xffe8bb },
        { x: 0.56, z: -0.08, rotation: 0.16, opacity: 0.034, color: 0xd8c7a1 },
      ];
      for (const [index, shaftSpec] of testimonyShafts.entries()) {
        const material = new THREE.MeshBasicMaterial({
          color: shaftSpec.color,
          transparent: true,
          opacity: shaftSpec.opacity,
          blending: THREE.AdditiveBlending,
          depthWrite: false,
          side: THREE.DoubleSide,
        });
        material.userData.baseOpacity = shaftSpec.opacity;
        const shaft = prop(new THREE.ConeGeometry(0.78 + index * 0.1, 3.8, 30, 1, true), material);
        shaft.name = `prop:testimony-light-shaft:${index}`;
        shaft.castShadow = false;
        shaft.receiveShadow = false;
        // The chapter-wide light veil already supplies the volumetric shafts.
        // Keep these authored alternates disabled: transparent cone sorting can
        // composite across Mary's robe in the close profile shot.
        shaft.visible = false;
        shaft.position.set(shaftSpec.x, 1.66, shaftSpec.z);
        shaft.rotation.z = shaftSpec.rotation;
        shaft.scale.z = 0.56;
        testimonyLight.add(shaft);
      }
      const testimonyDustPositions: number[] = [];
      for (let index = 0; index < 84; index += 1) {
        const phase = index * 2.399963229728653;
        const radius = 0.24 + (index % 15) * 0.052;
        testimonyDustPositions.push(
          Math.cos(phase) * radius,
          0.18 + ((index * 29) % 83) / 83 * 2.9,
          Math.sin(phase) * radius * 0.48,
        );
      }
      const testimonyDustGeometry = new THREE.BufferGeometry();
      testimonyDustGeometry.setAttribute('position', new THREE.Float32BufferAttribute(testimonyDustPositions, 3));
      const testimonyDust = new THREE.Points(
        testimonyDustGeometry,
        new THREE.PointsMaterial({
          color: 0xffe1a7,
          size: 0.028,
          transparent: true,
          opacity: 0.52,
          blending: THREE.AdditiveBlending,
          depthWrite: false,
        }),
      );
      testimonyDust.name = 'prop:testimony-light-dust';
      testimonyLight.add(testimonyDust);
      const testimonyGlow = new THREE.PointLight(0xffd79d, 0.75, 5.5, 1.8);
      testimonyGlow.position.set(0, 1.65, 0.25);
      testimonyLight.add(testimonyGlow);
      this.storyProps.add(testimonyLight);

      const emmaus = new THREE.Group();
      emmaus.name = 'prop:emmaus-table-legacy';
      const wood = cloth(0x4c3323);
      const top = prop(new THREE.BoxGeometry(2.65, 0.12, 1.05, 5, 1, 3), wood);
      top.position.y = 0.78;
      for (const x of [-1.05, 1.05]) {
        const leg = prop(new THREE.BoxGeometry(0.14, 0.75, 0.72), wood);
        leg.position.set(x, 0.38, 0);
        emmaus.add(leg);
      }
      const breadMaterial = cloth(0xa66e38);
      for (const x of [-0.18, 0.18]) {
        const bread = prop(new THREE.SphereGeometry(0.19, 14, 8), breadMaterial);
        bread.scale.set(1, 0.36, 0.72);
        bread.position.set(x, 0.89, 0.02);
        bread.rotation.y = x < 0 ? -0.28 : 0.28;
        emmaus.add(bread);
      }
      emmaus.position.set(0, 0, -2.02);
      emmaus.add(top);
      this.storyProps.add(emmaus);

      const lowMeal = createLowDiningTableAndCushions();
      lowMeal.name = 'prop:emmaus-table';
      lowMeal.scale.setScalar(0.72);
      lowMeal.position.set(0, 0, -2.05);

      const emmausInterior = new THREE.Group();
      emmausInterior.name = 'prop:emmaus-interior';
      const warmPractical = new THREE.PointLight(0xff9e53, 3.1, 9, 1.7);
      warmPractical.position.set(-1.7, 2.35, 0.5);
      const mealBreadMaterial = cloth(0xb97b3c);
      for (const x of [-0.18, 0.18]) {
        const bread = prop(new THREE.SphereGeometry(0.2, 14, 8), mealBreadMaterial);
        bread.name = x < 0 ? 'prop:emmaus-bread-held' : 'prop:emmaus-bread-table';
        bread.scale.set(1, 0.34, 0.72);
        bread.position.set(x < 0 ? -0.31 : 0.2, x < 0 ? 1.05 : 0.45, x < 0 ? -2.02 : -2.02);
        bread.rotation.y = x < 0 ? -0.24 : 0.24;
        emmausInterior.add(bread);
      }
      const hangingFabric = cloth(0x352627);
      hangingFabric.side = THREE.DoubleSide;
      for (const x of [-3.15, 3.15]) {
        const drape = prop(new THREE.PlaneGeometry(1.25, 4.6), hangingFabric);
        drape.position.set(x, 2.15, 0.65);
        drape.rotation.y = x < 0 ? 0.24 : -0.24;
        emmausInterior.add(drape);
      }
      emmausInterior.add(warmPractical);

      const woundMoment = new THREE.Group();
      woundMoment.name = 'prop:thomas-wound-moment';
      const palmWound = prop(new THREE.SphereGeometry(0.052, 12, 8), new THREE.MeshStandardMaterial({ color: 0x641f20, roughness: 0.94 }));
      palmWound.scale.set(0.88, 1.12, 0.36);
      palmWound.position.set(0, 0, 0);
      woundMoment.add(palmWound);

      const shoreline = new THREE.Group();
      shoreline.name = 'prop:shoreline-fire';
      const brazier = createCharcoalBrazierWithFish();
      brazier.position.set(0, 0, -0.48);
      const basket = createWovenBasket();
      basket.position.set(1.15, 0, -1.65);
      shoreline.add(brazier, basket);

      const stillness = new THREE.Group();
      stillness.name = 'prop:ascension-stillness';
      const lightField = prop(
        new THREE.PlaneGeometry(3.1, 5.6),
        new THREE.MeshBasicMaterial({ color: 0xffe2a2, transparent: true, opacity: 0.13, blending: THREE.AdditiveBlending, depthWrite: false, side: THREE.DoubleSide }),
      );
      lightField.name = 'prop:ascension-light-field';
      lightField.position.set(0, 2.3, -1.7);
      const stillLight = new THREE.PointLight(0xffdd9b, 2.4, 11, 1.5);
      stillLight.position.set(0, 2.6, -0.8);
      stillness.add(lightField, stillLight);

      const creationGift = new THREE.Group();
      creationGift.name = 'prop:creation-gift';
      const board = prop(new THREE.BoxGeometry(2.4, 0.12, 1.2), cloth(0x5b422d));
      board.position.set(0, 0.46, -1.55);
      const giftBread = prop(new THREE.SphereGeometry(0.34, 16, 9), mealBreadMaterial);
      giftBread.scale.set(1.35, 0.36, 0.82);
      giftBread.position.set(-0.48, 0.61, -1.52);
      const bowl = prop(new THREE.CylinderGeometry(0.38, 0.28, 0.15, 18, 2, true), cloth(0x765e48));
      bowl.position.set(0.55, 0.58, -1.48);
      const water = prop(new THREE.CircleGeometry(0.32, 20), new THREE.MeshStandardMaterial({ color: 0x78939c, roughness: 0.16, transparent: true, opacity: 0.86 }));
      water.rotation.x = -Math.PI / 2;
      water.position.set(0.55, 0.665, -1.48);
      creationGift.add(board, giftBread, bowl, water);

      this.storyProps.add(lowMeal, emmausInterior, woundMoment, shoreline, stillness, creationGift);
    }
  }

  private evaluateStoryProps(time: number): void {
    for (const child of this.storyProps.children) child.visible = false;
    if (this.id === 'bread-and-night') {
      const dining = this.storyProps.getObjectByName('prop:last-supper-setting');
      if (dining) dining.visible = true;
      const service = this.storyProps.getObjectByName('prop:footwashing');
      if (service) service.visible = time >= 11 && time < 21;
      const threshold = this.storyProps.getObjectByName('prop:judas-threshold');
      if (threshold) threshold.visible = time >= 37;
      const exitLamp = this.storyProps.getObjectByName('prop:judas-exit-lamp');
      const flame = exitLamp?.getObjectByName('prop:oil-lamp:flame') as THREE.Mesh | undefined;
      const lampLight = exitLamp?.getObjectByName('prop:judas-exit-lamp-light') as THREE.PointLight | undefined;
      const dying = smootherstep((time - 40.5) / 4.25);
      if (flame) {
        flame.visible = dying < 0.98;
        flame.scale.set(1 - dying * 0.82, 1 - dying * 0.96, 1 - dying * 0.82);
        const material = flame.material as THREE.MeshStandardMaterial;
        material.emissiveIntensity = 1.35 * (1 - dying);
      }
      if (lampLight) lampLight.intensity = 3.8 * (1 - dying);
    } else if (this.id === 'garden-and-judgment') {
      const night = this.storyProps.getObjectByName('prop:gethsemane-night');
      if (night) night.visible = time < 31;
      const arrest = smootherstep((time - 21) / 10);
      const torchLeft = this.storyProps.getObjectByName('prop:arrest-torch:left');
      const torchRight = this.storyProps.getObjectByName('prop:arrest-torch:right');
      if (torchLeft) {
        torchLeft.visible = time >= 21 && time < 31;
        torchLeft.position.set(THREE.MathUtils.lerp(-4.4, -1.18, arrest) - 0.42, 0, THREE.MathUtils.lerp(0.05, -1.42, arrest) + 0.12);
      }
      if (torchRight) {
        torchRight.visible = time >= 21 && time < 31;
        torchRight.position.set(THREE.MathUtils.lerp(4.6, 1.2, arrest) + 0.42, 0, THREE.MathUtils.lerp(0.2, -1.35, arrest) + 0.12);
      }
      const judgment = this.storyProps.getObjectByName('prop:roman-judgment');
      if (judgment) judgment.visible = time >= 42;
      const authority = this.storyProps.getObjectByName('prop:trial-roman-authority');
      if (authority) authority.visible = time >= 42;
    } else if (this.id === 'golgotha') {
      const procession = this.storyProps.getObjectByName('prop:road-procession');
      if (procession) procession.visible = time < 26;
      const executionAuthority = this.storyProps.getObjectByName('prop:execution-roman-authority');
      if (executionAuthority) executionAuthority.visible = time >= 26 && time < 106;
      const witnessSilhouette = this.storyProps.getObjectByName('prop:mary-witness-silhouette');
      if (witnessSilhouette) {
        witnessSilhouette.visible = (time >= 26 && time < 40) || time >= 106;
        witnessSilhouette.position.set(time >= 106 ? -3.1 : -4.05, 0, time >= 106 ? 0.15 : 1.05);
        witnessSilhouette.rotation.y = time >= 106 ? 2.5 : 2.35;
      }
      if (time < 26) {
        const progress = smootherstep(time / 26);
        const roadZ = THREE.MathUtils.lerp(-2.55, 1.15, progress);
        const stumble = Math.sin(clamp01((time - 6.2) / 5.5) * Math.PI) * (1 - smoothstep((time - 13) / 3));
        const beam = procession?.getObjectByName('prop:procession-crossbeam');
        if (beam) {
          beam.position.set(0, 1.62 - stumble * 0.08, roadZ - 0.13);
          beam.rotation.set(0.035 + stumble * 0.05, 0.015, 0.105 + stumble * 0.07);
        }
        const leftSpear = procession?.getObjectByName('prop:road-spear:left');
        if (leftSpear) {
          leftSpear.position.set(-1.86, 0, roadZ - 1.3);
          leftSpear.rotation.z = -0.055;
        }
        const rightSpear = procession?.getObjectByName('prop:road-spear:right');
        if (rightSpear) {
          rightSpear.position.set(1.9, 0, roadZ - 1.14);
          rightSpear.rotation.z = 0.05;
        }
      }
    } else if (this.id === 'silence') {
      const burial = this.storyProps.getObjectByName('prop:burial-linen');
      if (burial) {
        burial.visible = time < 13;
        const settle = smootherstep(time / 9);
        const body = burial.getObjectByName('prop:tomb-bier:wrapped-body');
        const head = burial.getObjectByName('prop:tomb-bier:head-wrap');
        if (body) {
          body.position.y = THREE.MathUtils.lerp(0.98, 0.72, settle);
          body.rotation.z = -(1 - settle) * 0.035;
        }
        if (head) head.position.y = THREE.MathUtils.lerp(0.98, 0.74, settle);
      }
      const stone = this.environment.getObjectByName('set:tomb-stone');
      if (stone) {
        const seal = smoothstep((time - 13) / 11);
        stone.position.x = time < 13 ? 2.2 : THREE.MathUtils.lerp(2.2, 0, seal);
        stone.rotation.z = -seal * Math.PI * 1.55;
      }
      const memory = this.storyProps.getObjectByName('prop:vision-memory');
      if (memory) {
        memory.visible = time >= 40 && time < 52;
        const dissolve = smootherstep((time - 47) / 5);
        const silhouette = memory.getObjectByName('prop:vision-human-memory');
        if (silhouette) {
          silhouette.position.y = Math.sin(time * 0.34) * 0.035;
          silhouette.rotation.z = -0.045 + Math.sin(time * 0.2) * 0.018;
          silhouette.traverse((child) => {
            if ((child instanceof THREE.Mesh || child instanceof THREE.Points) && child.material && !Array.isArray(child.material)) {
              const material = child.material as THREE.Material & { opacity?: number; userData: Record<string, unknown> };
              if (typeof material.opacity === 'number') {
                const baseOpacity = typeof material.userData.baseOpacity === 'number' ? material.userData.baseOpacity : material.opacity;
                material.opacity = baseOpacity * (1 - dissolve * 0.92);
              }
            }
          });
        }
        const enteringLight = memory.getObjectByName('prop:vision-entering-light') as THREE.Mesh | undefined;
        if (enteringLight) {
          enteringLight.visible = true;
          enteringLight.scale.set(0.8 + dissolve * 1.4, 1, 1);
          (enteringLight.material as THREE.MeshBasicMaterial).opacity = 0.04 + dissolve * 0.26;
        }
      }
      const sabbathFrame = this.storyProps.getObjectByName('prop:sabbath-city-frame');
      if (sabbathFrame) sabbathFrame.visible = time >= 24 && time < 40 || time >= 52;
    } else if (this.id === 'garden-reopened') {
      const linen = this.storyProps.getObjectByName('prop:empty-tomb-linen');
      if (linen) linen.visible = time < 27;
      const approachDress = this.storyProps.getObjectByName('prop:dawn-approach-dress');
      if (approachDress) approachDress.visible = time < 26;
      const stone = this.environment.getObjectByName('set:tomb-stone');
      if (stone) {
        stone.visible = time < 27;
        stone.position.x = 2.2;
        stone.rotation.z = -Math.PI * 0.72;
      }
    } else if (this.id === 'eyes-opened') {
      const testimonyLight = this.storyProps.getObjectByName('prop:testimony-volumetric-light');
      if (testimonyLight) {
        testimonyLight.visible = time < 23;
        testimonyLight.rotation.y = Math.sin(time * 0.12) * 0.025;
        testimonyLight.children.forEach((child, index) => {
          if (child instanceof THREE.Mesh && child.material instanceof THREE.MeshBasicMaterial) {
            const baseOpacity = typeof child.material.userData.baseOpacity === 'number'
              ? child.material.userData.baseOpacity
              : child.material.opacity;
            child.material.opacity = baseOpacity * (0.88 + Math.sin(time * 0.18 + index * 1.6) * 0.12);
          }
        });
      }
      const emmaus = this.storyProps.getObjectByName('prop:emmaus-table');
      if (emmaus) emmaus.visible = time >= 23 && time < 37;
      const interior = this.storyProps.getObjectByName('prop:emmaus-interior');
      if (interior) interior.visible = time >= 23 && time < 54;
      const heldBread = this.storyProps.getObjectByName('prop:emmaus-bread-held');
      const tableBread = this.storyProps.getObjectByName('prop:emmaus-bread-table');
      if (heldBread) heldBread.visible = false;
      if (tableBread) tableBread.visible = time >= 23 && time < 37;
      const woundMoment = this.storyProps.getObjectByName('prop:thomas-wound-moment');
      if (woundMoment) woundMoment.visible = false;
      const shoreline = this.storyProps.getObjectByName('prop:shoreline-fire');
      if (shoreline) shoreline.visible = time >= 54 && time < 66;
      const stillness = this.storyProps.getObjectByName('prop:ascension-stillness');
      if (stillness) {
        stillness.visible = time >= 66 && time < 76;
        const field = stillness.getObjectByName('prop:ascension-light-field') as THREE.Mesh | undefined;
        if (field) {
          const presence = 1 - smoothstep((time - 72) / 4);
          (field.material as THREE.MeshBasicMaterial).opacity = 0.06 + presence * 0.13;
          field.scale.set(1 + (1 - presence) * 0.3, 1, 1);
        }
      }
      const creationGift = this.storyProps.getObjectByName('prop:creation-gift');
      if (creationGift) creationGift.visible = time >= 76;
    }
  }

  private setEnvironmentVisibility(name: string, visible: boolean): void {
    const section = this.environment.getObjectByName(name);
    if (section) section.visible = visible;
  }

  private configureShadows(): void {
    const size = this.quality.shadowMapSize;
    this.keyLight.shadow.mapSize.set(size, size);
    this.keyLight.shadow.camera.left = -12;
    this.keyLight.shadow.camera.right = 12;
    this.keyLight.shadow.camera.top = 12;
    this.keyLight.shadow.camera.bottom = -8;
    this.keyLight.shadow.camera.near = 0.5;
    this.keyLight.shadow.camera.far = 60;
    this.keyLight.shadow.bias = -0.0008;
    this.keyLight.shadow.normalBias = 0.025;
    this.keyLight.shadow.map?.dispose();
    this.keyLight.shadow.map = null;
  }

  private createLightVeil(color: number): THREE.Group {
    const root = new THREE.Group();
    root.name = 'lighting:veil';
    for (let index = 0; index < 3; index += 1) {
      const material = new THREE.MeshBasicMaterial({
        color,
        transparent: true,
        opacity: 0.035,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
        side: THREE.DoubleSide,
      });
      const plane = new THREE.Mesh(new THREE.PlaneGeometry(3.5 + index * 2.5, 16), material);
      plane.position.set(-5 + index * 4.7, 5, -3 - index * 2);
      plane.rotation.set(0.15, -0.35 + index * 0.24, -0.18 + index * 0.16);
      root.add(plane);
    }
    return root;
  }

  private evaluateLightVeil(time: number, reducedMotion: boolean): void {
    const chapterGlow = this.id === 'garden-reopened' || this.id === 'eyes-opened' ? 1.8 : this.id === 'silence' ? 0.3 : 1;
    this.lightVeil.children.forEach((child, index) => {
      const mesh = child as THREE.Mesh<THREE.PlaneGeometry, THREE.MeshBasicMaterial>;
      mesh.material.opacity = chapterGlow * (0.018 + Math.sin(time * 0.11 + index * 1.7) * (reducedMotion ? 0.002 : 0.006));
    });
  }
}
